import { useCallback, useMemo, useState } from 'react';

import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';

const DEFAULT_PAGE_SIZE = 10;

export const BOM_PICKER_BASE_FILTERS = {
  status: 'ACTIVE',
};

/**
 * BOM 選取 Dialog 本地 listQuery（不寫 URL）。status 固定 ACTIVE；
 * includeInactive 時不鎖 status（報價單可匯入停用 BOM）。
 */
function useBomPickerListQuery({ includeInactive = false } = {}) {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [keyword, setKeywordState] = useState('');
  const [sort, setSortState] = useState([]);
  const [userFilters, setUserFilters] = useState({});

  const baseFilters = useMemo(
    () => (includeInactive ? {} : BOM_PICKER_BASE_FILTERS),
    [includeInactive],
  );

  const lockedFilterKeys = useMemo(
    () => new Set(Object.keys(baseFilters)),
    [baseFilters],
  );

  const listQuery = useMemo(() => ({
    page,
    pageSize,
    keyword,
    sort,
    filters: {
      ...baseFilters,
      ...userFilters,
    },
  }), [page, pageSize, keyword, sort, userFilters, baseFilters]);

  const setPage = useCallback((nextPage) => {
    setPageState(nextPage);
  }, []);

  const setPageSize = useCallback((nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPageState(1);
  }, []);

  const setKeyword = useCallback((nextKeyword) => {
    setKeywordState(nextKeyword ?? '');
    setPageState(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    if (lockedFilterKeys.has(key)) {
      return;
    }
    setUserFilters((prev) => mergeFilterUpdate(prev, key, value));
    setPageState(1);
  }, [lockedFilterKeys]);

  const setSort = useCallback((nextSort) => {
    setSortState(nextSort ?? []);
    setPageState(1);
  }, []);

  const removeSortAt = useCallback((index) => {
    setSortState((prev) => (prev ?? []).filter((_, sortIndex) => sortIndex !== index));
    setPageState(1);
  }, []);

  const resetQuery = useCallback(() => {
    setPageState(1);
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setKeywordState('');
    setSortState([]);
    setUserFilters({});
  }, []);

  return {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  };
}

export default useBomPickerListQuery;
